import { useCallback, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

/**
 * Safely access the `react-hook-form` context.
 * Throws an error if the component is not wrapped in a `FormProvider`.
 */
export function useFormContextSafe() {
  const formContext = useFormContext();
  if (!formContext) {
    throw new Error('This component must be used within a FormProvider.');
  }
  return formContext;
}

/**
 * Re-runs validation when the `disabled` prop changes.
 *
 * When a field goes from disabled to enabled, we need to re-validate
 * so any errors show immediately.
 * @param {string} fieldName - The form field name to validate
 * @param {boolean} [disabled] - The current disabled state
 */
export function useTriggerOnDisabledChange(fieldName, disabled) {
  const { trigger } = useFormContextSafe();

  useEffect(() => {
    if (disabled !== undefined) {
      trigger(fieldName);
    }
  }, [disabled, trigger, fieldName]);
}

/**
 * A convenience hook for form fields.
 * Handles common boilerplate: context access, disabled re-validation, error extraction,
 * and provides helpers for clearing/validating fields.
 *
 * @param {string} name - The field name in the form
 * @param {boolean} [disabled] - Whether the field is disabled
 */
export function useFormField(name, disabled) {
  const { register, formState, trigger, watch, setValue, control, getValues } =
    useFormContextSafe();

  // Auto re-validate when disabled changes
  useTriggerOnDisabledChange(name, disabled);

  // Watch the current value
  const currentValue = watch(name);

  // Extract error message from formState
  const fieldError = formState.errors?.[name];
  const errorMessage =
    typeof fieldError?.message === 'string' ? fieldError.message : '';

  const defaultValue = formState.defaultValues?.[name];

  /**
   * Clear the field back to its default value.
   * Does not trigger validation (you can call validateField after if needed).
   */
  const clearField = useCallback(() => {
    setValue(name, defaultValue, {
      shouldValidate: false,
      shouldDirty: true,
    });
  }, [setValue, name, defaultValue]);

  /**
   * Manually trigger validation for this field.
   */
  const validateField = useCallback(() => {
    return trigger(name);
  }, [trigger, name]);

  /**
   * Keyboard handler for common field shortcuts:
   * - Escape: Clear field to default value
   * - Enter: Trigger validation
   *
   * @param {React.KeyboardEvent} e - The keyboard event
   */
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Escape' && currentValue) {
        e.preventDefault();
        e.stopPropagation();
        clearField();
      }

      if (e.key === 'Enter' && currentValue) {
        e.preventDefault();
        e.stopPropagation();
        validateField();
      }
    },
    [currentValue, clearField, validateField],
  );

  return {
    // Field state
    errorMessage,
    currentValue,

    // Field actions
    clearField,
    validateField,
    handleKeyDown,

    // RHF methods (for advanced use)
    register,
    watch,
    setValue,
    trigger,
    control,
    formState,
    getValues,
  };
}
